import React, { ReactNode, useEffect, useRef } from 'react';
import { FormicaSelectBoxItem } from './FormicaSearchSelectBox';
import { createPortal } from 'react-dom';
import Icon from 'icons/Icon';
import CustomCheckBox from './CustomCheckBox';
import LanguageUtil from '../../util/LanguageUtil';

function FormicaMultiSelectBox(props: FormicaMultiSelectBoxProps) {
  const [search, setSearch] = React.useState<string>('');
  const [searchResults, setSearchResults] = React.useState<FormicaSelectBoxItem[]>([]);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const selectBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside); // mount
    return () => document.removeEventListener('click', handleClickOutside); // unmount
  }, []);

  useEffect(() => {
    if (search == '' || search.trim() == '') {
      setSearchResults(props.options);
      return;
    }
    const filteredOptions: FormicaSelectBoxItem[] = [];
    props.options.forEach((option: FormicaSelectBoxItem) => {
      if (option.label.toLowerCase().includes(search.toLowerCase()) || option.value.toLowerCase().includes(search.toLowerCase())) {
        filteredOptions.push(option);
      }
    });
    setSearchResults(filteredOptions);
  }, [search, props.options]);

  const getCustomSize = (customSize: 'x-small' | 'small' | 'medium' | 'large') => {
    switch (customSize) {
      case 'x-small':
        return 'h-[24px] min-h-[24px]';
      case 'small':
        return 'h-[28px] min-h-[28px]';
      case 'medium':
        return 'h-[34px] min-h-[34px]';
      case 'large':
        return 'h-[40px] min-h-[40px]';
      default:
        return 'h-[34px] min-h-[34px]';
    }
  };

  const handleClickOutside = (event: any) => {
    if (selectBoxRef.current && !selectBoxRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearch('');
    }
  };

  const onClickMenuItem = (item: FormicaSelectBoxItem) => {
    const isExist = props.selectedItems?.find((selectedItem: FormicaSelectBoxItem) => selectedItem.value == item.value);
    if (!isExist) {
      props.onChange([...props.selectedItems, item]);
      setSearch('');
    } else {
      props.onChange(props.selectedItems.filter((selectedItem: FormicaSelectBoxItem) => selectedItem.value != item.value));
      setSearch('');
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.querySelector('.scrollable-editor-container')?.classList.remove('overflow-y-scroll');
      if (!document.querySelector('.scrollable-editor-container')?.classList.contains('overflow-y-hidden')) {
        document.querySelector('.scrollable-editor-container')?.classList.add('overflow-y-hidden');
      }
    } else {
      if (!document.querySelector('.scrollable-editor-container')?.classList.contains('overflow-y-scroll')) {
        document.querySelector('.scrollable-editor-container')?.classList.add('overflow-y-scroll');
      }
      document.querySelector('.scrollable-editor-container')?.classList.remove('overflow-y-hidden');
    }
  }, [isOpen]);

  const getMenu = (): ReactNode => {
    const { extraHeaderNode, extraFooterNode } = props;
    const topSpace = props.customSize == 'x-small' ? 24 : props.customSize == 'small' ? 28 : props.customSize == 'medium' ? 34 : 40;
    return (
      <div
        style={{
          zIndex: 9999,
          top: selectBoxRef?.current?.getBoundingClientRect().top! + topSpace + 'px',
          width: selectBoxRef?.current?.getBoundingClientRect().width + 'px',
          left: selectBoxRef?.current?.getBoundingClientRect().left + 'px',
        }}
        onClick={(e) => e.stopPropagation()}
        className={`${isOpen ? 'visible' : 'hidden'} flex flex-col bg-white max-h-[240px] overflow-hidden absolute border-[1px] not-dismissable`}
      >
        <div className={'flex items-center w-full h-10 gap-x-1 px-1 py-1 border-b-[1px] border-[#E0E4E8]'}>
          <Icon size={20} icon={'search'} className={'text-f-black-3'} />
          <input
            className={'flex w-full h-full border-none outline-none text-sm'}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.search', defaultMessage: 'Search' })}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
        {extraHeaderNode && (
          <div className={'flex items-center justify-start bg-main-gray flex-shrink-0 w-full overflow-scroll h-10 border-t border-[#E0E4E8] px-3'}>
            {extraHeaderNode}
          </div>
        )}
        <div className={'flex flex-col items-start max-h-[155px] overflow-scroll justify-start gap-x-1'}>
          {searchResults && searchResults.length <= 0 ? (
            <div className={'flex items-center justify-start text-f-black-3  w-full h-16 '}>
              <Icon icon={'no-folder'} size={20} />
              <span className={'text-sm'}>{LanguageUtil.get({ id: 'common.label.noResultsFound', defaultMessage: 'No results found' })}</span>
            </div>
          ) : (
            searchResults?.map((item, index) => {
              const isExist = props.selectedItems?.find((selectedItem: FormicaSelectBoxItem) => selectedItem.value == item.value);
              return (
                <div
                  key={index}
                  className={`flex w-full text-sm bg- text-f-black-2 cursor-pointer h-[40px] min-h-[40px] items-center hover:text-f-purple justify-start gap-x-2 px-3 py-2 hover:bg-[#F7F8FA]`}
                >
                  <CustomCheckBox checked={!!isExist} onChangeChecked={() => onClickMenuItem(item)}></CustomCheckBox>
                  {item.label}
                </div>
              );
            })
          )}
        </div>
        {extraFooterNode && (
          <div className={'flex items-center justify-start bg-main-gray flex-shrink-0 w-full overflow-scroll h-10 border-t border-[#E0E4E8] px-3'}>
            {extraFooterNode}
          </div>
        )}
      </div>
    );
  };

  const getSelectedItemsTemplate = (): ReactNode => {
    const maxTagSize = props.maxTagSize ?? 2;
    const nodes: ReactNode[] = [];
    if (props.selectedItems?.length == 0) {
      nodes.push(
        <p key={0} className={'flex w-full h-full text-f-black-3'}>
          {props.placeholder || 'Select'}
        </p>,
      );
      return nodes;
    }
    for (let i = 0; i < props.selectedItems?.length; i++) {
      if (i > maxTagSize) {
        break;
      }
      if (i == maxTagSize) {
        nodes.push(
          <div
            key={i}
            className={
              'flex items-center justify-center w-6 h-6 cursor-default rounded-full bg-secondary shrink-0 border border-f-purple text-sm text-f-purple'
            }
          >
            +{props.selectedItems.length - maxTagSize}
          </div>,
        );
        break;
      }
      nodes.push(
        <div
          key={i}
          className={'flex  items-center justify-around px-2 overflow-hidden gap-x-1 cursor-default rounded-lg bg-secondary border border-f-purple text-sm'}
        >
          <p className={'inline-flex w-full h-full text-f-purple text-sm truncate'}>{props.selectedItems[i].label}</p>
          <span className={'cursor-pointer text-f-purple flex w-5 items-center  justify-center'} onClick={() => onClickMenuItem(props.selectedItems[i])}>
            <Icon icon={'close'} size={20} />
          </span>
        </div>,
      );
    }
    return nodes;
  };

  return (
    <div
      ref={selectBoxRef}
      onClick={() => setIsOpen(true)}
      onFocus={() => console.log('focus')}
      className={`flex ${props.disabled ? 'opacity-50 cursor-not-allowed hover:border-f-black-5' : 'cursor-pointer'} ${isOpen && 'formica-shadow border-f-purple'} border-f-black-5 shadow-sm shadow-black/2 bg-white  text-md select-none focus-within:border-f-purple focus-within:formica-shadow items-center justify-start ${getCustomSize(props.customSize || 'medium')}  ${props.rounded ? 'rounded-[4px]' : 'rounded-[2px]'} relative  border-[1px] w-full group hover:border-f-purple ${props.selectBoxClassName && props.selectBoxClassName}  `}
    >
      <div className={'flex items-center w-full gap-x-1.5 overflow-scroll max-w-full px-2'}>{getSelectedItemsTemplate()}</div>
      <div className={'flex items-center justify-start w-3 mr-1'}>
        <Icon icon={'arrow-down'} size={20} className={'text-f-black-3 group-focus:text-f-purple'} />
      </div>
      {isOpen && createPortal(getMenu(), document.body)}
    </div>
  );
}

interface FormicaMultiSelectBoxProps {
  selectedItems: FormicaSelectBoxItem[];
  onChange: (selectedItems: FormicaSelectBoxItem[]) => void;
  options: FormicaSelectBoxItem[];
  disabled?: boolean;
  rounded?: boolean;
  selectBoxClassName?: string;
  customSize?: 'x-small' | 'small' | 'medium' | 'large';
  placeholder?: string;
  maxTagSize?: number;
  extraHeaderNode?: ReactNode;
  extraFooterNode?: ReactNode;
}

export default FormicaMultiSelectBox;
